import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';

const defaultConfig = {
  phone: '919747760241',
  msg: 'Hi! I’d like to know more.',
  theme: 'light',
  position: 'right',
  autopopup: 3000,
  autoclose: 15000,
};

function App() {
  const [config, setConfig] = useState(defaultConfig);
  const [scriptVisible, setScriptVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const scriptRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setConfig({ ...config, [name]: value });
    setCopied(false);
  };

  const generateScript = useCallback(() => {
    const { phone, msg, theme, position, autopopup, autoclose } = config;
    return `<script src="https://chat.infotwistsolutions.com/wa-widget.js?phone=${phone}&msg=${encodeURIComponent(
      msg
    )}&theme=${theme}&position=${position}&autopopup=${autopopup}&autoclose=${autoclose}" defer></script>`;
  }, [config]);

  const handleCopy = () => {
    navigator.clipboard.writeText(generateScript());
    setCopied(true);
  };

  useEffect(() => {
    const { phone, msg, theme, position, autopopup, autoclose } = config;

    if (!phone || phone.trim() === "") return;

    if (scriptRef.current) scriptRef.current.remove();

    const oldBubble = document.getElementById("wa-bubble");
    const oldChatbox = document.getElementById("wa-chatbox");
    const oldStyle = document.getElementById("wa-style");

    if (oldBubble) oldBubble.remove();
    if (oldChatbox) oldChatbox.remove();
    if (oldStyle) oldStyle.remove();

    setTimeout(() => {
      const script = document.createElement("script");
      script.src = `/wa-widget.js?phone=${phone}&msg=${encodeURIComponent(
        msg
      )}&theme=${theme}&position=${position}&autopopup=${autopopup}&autoclose=${autoclose}`;
      script.defer = true;
      scriptRef.current = script;
      document.body.appendChild(script);
    }, 50);
  }, [config]);

  return (
    <div className="min-h-screen primary-bg p-6">
      <h1 className="text-2xl font-bold mb-4 text-center text-green-600 flex flex-col items-center">
        <svg clip-rule="evenodd" fill-rule="evenodd" height="60" image-rendering="optimizeQuality" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" viewBox="0 0 512 512" width="60" xmlns="http://www.w3.org/2000/svg" className="mb-4"><linearGradient id="a" gradientUnits="userSpaceOnUse" x1="67.83" x2="474.19" y1="82.42" y2="389.98"><stop offset="0" stop-color="#39ae41"/><stop offset="1" stop-color="#80c269"/></linearGradient><path d="M256 0c141.39 0 256 114.61 256 256S397.39 512 256 512 0 397.39 0 256 114.61 0 256 0" fill="url(#a)"/><g fill="#fff"><path d="M256 373.82c-20.85 0-41.37-5.55-59.32-16.04a12.54 12.54 0 0 0-9.6-1.26l-39.03 10.71 13.6-29.95c1.85-4.07 1.37-8.82-1.24-12.45-14.55-20.15-22.23-43.96-22.23-68.83 0-64.97 52.85-117.82 117.82-117.82S373.82 191.03 373.82 256 320.97 373.82 256 373.82m0-260.58c-78.72 0-142.75 64.04-142.75 142.76 0 27.69 7.85 54.28 22.8 77.36l-21.69 47.77c-2 4.41-1.27 9.57 1.86 13.25 2.41 2.81 5.9 4.38 9.49 4.38 8.04 0 51.89-13.78 63.05-16.85A142.6 142.6 0 0 0 256 398.76c78.71 0 142.76-64.05 142.76-142.76 0-78.72-64.05-142.75-142.76-142.76"/><path d="M290.08 272.69c-5.4 2.21-8.85 10.67-12.35 14.99-1.8 2.21-3.94 2.56-6.7 1.45-20.28-8.08-35.83-21.62-47.02-40.28-1.9-2.89-1.56-5.18.73-7.87 3.38-3.98 7.63-8.5 8.54-13.87 2.03-11.86-13.48-48.67-33.97-31.99-58.95 48.04 98.34 175.45 126.73 106.54 8.03-19.54-27-32.64-35.96-28.97"/></g></svg>
        WhatsApp Widget Configurator
      </h1>

      <div className="max-w-xl mx-auto bg-white shadow rounded-lg p-6 space-y-4">
        <div>
          <label className="block font-medium">WhatsApp Number:</label>
          <input
            type="text"
            name="phone"
            value={config.phone}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mt-1"
            placeholder="e.g. 919876543210"
          />
        </div>

        <div>
          <label className="block font-medium">Base Message:</label>
          <input
            type="text"
            name="msg"
            value={config.msg}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mt-1"
          />
        </div>

        <div>
          <label className="block font-medium">Theme:</label>
          <select
            name="theme"
            value={config.theme}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mt-1"
          >
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
        </div>

        <div>
          <label className="block font-medium">Position:</label>
          <select
            name="position"
            value={config.position}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mt-1"
          >
            <option value="left">Left Bottom</option>
            <option value="center">Center Bottom</option>
            <option value="right">Right Bottom</option>
            <option value="left-middle">Left Middle</option>
            <option value="center-middle">Center Middle</option>
            <option value="right-middle">Right Middle</option>
          </select>
        </div>

        <div>
          <label className="block font-medium">Auto Popup Delay (ms):</label>
          <input
            type="number"
            name="autopopup"
            value={config.autopopup}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mt-1"
            min="0"
          />
        </div>

        <div>
          <label className="block font-medium">Auto Popup Close Delay (ms):</label>
          <input
            type="number"
            name="autoclose"
            value={config.autoclose}
            onChange={handleChange}
            className="w-full border rounded px-3 py-2 mt-1"
            min="0"
          />
        </div>

        <div className="flex justify-center">
          <button
            onClick={() => setScriptVisible(true)}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded shadow flex items-center gap-2"
          >
            <svg width="20px" height="20px" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" fill="white"><path d="M511.4 38.222C510.291 17.884 494.116 1.711 473.778.601 432.74-1.641 352.436.54 275.648 40.257c-39.145 20.248-80.545 54.577-113.584 94.185-.407.488-.803.979-1.207 1.468l-74.98 5.792c-12.342.954-23.335 7.423-30.161 17.747L4.562 236.821c-5.177 7.83-6 17.629-2.203 26.212 3.798 8.584 11.602 14.566 20.877 16.003l63.171 9.784c-.223 1.228-.447 2.455-.652 3.683-2.103 12.58 2.065 25.514 11.151 34.599l87.992 87.993c7.533 7.533 17.712 11.686 28.142 11.686a39 39 0 0 0 6.458-.536c1.228-.205 2.455-.429 3.683-.652l9.784 63.172c1.437 9.275 7.419 17.08 16.001 20.877a27.4 27.4 0 0 0 11.112 2.36 27.35 27.35 0 0 0 15.101-4.562l77.372-51.155c10.325-6.827 16.793-17.82 17.745-30.161l5.792-74.979c.489-.404.981-.8 1.469-1.207 39.609-33.039 73.939-74.439 94.186-113.585 39.719-76.791 41.896-157.096 39.657-198.131M336.006 431.259l-74.011 48.933-9.536-61.565c31.28-9.197 62.223-23.927 91.702-43.66l-3.773 48.845a9.77 9.77 0 0 1-4.382 7.447m-129.895-37.377-87.993-87.993c-2.245-2.246-3.283-5.401-2.774-8.44 2.616-15.643 6.681-30.534 11.713-44.562l132.028 132.028c-16.848 6.035-31.939 9.635-44.534 11.741-3.044.506-6.195-.529-8.44-2.774M88.188 171.613l48.844-3.773c-19.734 29.479-34.464 60.422-43.661 91.702l-61.564-9.535 48.934-74.012a9.77 9.77 0 0 1 7.447-4.382m270.155 155.286c-24.233 20.213-47.756 34.833-69.438 45.412L139.684 223.09c13.858-28.304 30.771-51.873 45.417-69.431 30.575-36.655 68.602-68.276 104.331-86.756 70.474-36.453 144.725-38.416 182.713-36.348a9.84 9.84 0 0 1 9.301 9.302c2.071 37.988.104 112.238-36.349 182.713-18.479 35.728-50.1 73.754-86.754 104.329"/><path d="M350.721 236.243c19.202-.002 38.412-7.312 53.031-21.931 14.166-14.165 21.966-32.999 21.966-53.031s-7.801-38.866-21.966-53.031c-29.242-29.243-76.822-29.241-106.062 0-14.166 14.165-21.967 32.999-21.967 53.031s7.802 38.866 21.967 53.031c14.622 14.622 33.822 21.933 53.031 21.931m-31.82-106.781c8.772-8.773 20.295-13.159 31.818-13.159s23.047 4.386 31.819 13.159c8.499 8.499 13.179 19.799 13.179 31.818s-4.68 23.32-13.179 31.819c-17.544 17.545-46.093 17.544-63.638 0-8.499-8.499-13.18-19.799-13.18-31.818s4.682-23.32 13.181-31.819m-303.6 292.476c3.839 0 7.678-1.464 10.606-4.394l48.973-48.973c5.858-5.858 5.858-15.355 0-21.213s-15.355-5.858-21.213 0L4.695 396.331c-5.858 5.858-5.858 15.355 0 21.213a14.95 14.95 0 0 0 10.606 4.394m104.46-29.699c-5.857-5.858-15.355-5.858-21.213 0L4.394 486.394c-5.858 5.858-5.858 15.355 0 21.213C7.323 510.536 11.161 512 15 512s7.678-1.464 10.606-4.394l94.154-94.154c5.859-5.858 5.859-15.355.001-21.213m23.668 44.881-48.973 48.973c-5.858 5.858-5.858 15.355 0 21.213 2.929 2.929 6.768 4.394 10.606 4.394s7.678-1.464 10.606-4.394l48.973-48.973c5.858-5.858 5.858-15.355 0-21.213s-15.355-5.858-21.212 0"/></svg> Generate Script
          </button>
        </div>

        {scriptVisible && (
          <div className="relative">
            <label className="block font-medium">Generated Script:</label>
            <textarea
              readOnly
              className="w-full h-28 border rounded px-3 py-2 mt-1 bg-gray-100 text-sm cursor-pointer"
              value={generateScript()}
              onClick={() => {
                handleCopy();
                setTimeout(() => setCopied(false), 1500);
              }}
            />
            {copied && (
              <div className="absolute top-0 right-0 mt-1 mr-2 text-xs text-green-600 bg-white border px-2 py-1 rounded shadow flex items-center gap-1">
                <svg viewBox="0 0 4000 4000" xmlns="http://www.w3.org/2000/svg" width="20" height="20"><path d="M1345 3275h1100c181.973 0 330-148.027 330-330V1405c0-181.973-148.027-330-330-330H1345c-181.973 0-330 148.027-330 330v1540c0 181.973 148.027 330 330 330m-110-1870c0-60.64 49.307-110 110-110h1100c60.693 0 110 49.36 110 110v1540c0 60.64-49.307 110-110 110H1345c-60.693 0-110-49.36-110-110z"/><path d="M3215 2065V965c0-181.973-148.027-330-330-330H1785c-60.747 0-110 49.253-110 110s49.253 110 110 110h1100c60.693 0 110 49.36 110 110v1100c0 60.747 49.253 110 110 110s110-49.253 110-110"/></svg> Code copied to clipboard
              </div>
            )}
          </div>
        )}

        {scriptVisible && (
          <>
            <div className="mt-6 p-4 bg-white border rounded shadow-md">
              <h2 className="text-lg font-semibold text-green-700 mb-2">
                📋 How to Implement This Widget
              </h2>
              <ol className="list-decimal pl-5 text-sm space-y-2 text-gray-700">
                <li>
                  <strong>Copy the Generated Script:</strong> Above is your customized WhatsApp widget code.
                </li>
                <li>
                  <strong>Paste it into Your Website:</strong> Add the script <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">before the &lt;/body&gt;</code> tag.
                </li>
                <li>
                  <strong>Save and Refresh:</strong> Reload your website and you’ll see the WhatsApp bubble.
                </li>
              </ol>

              <h3 className="font-medium mt-4 mb-1 text-green-700">⚙️ Parameters Explained:</h3>
              <ul className="list-disc pl-5 text-sm space-y-1">
                <li><code>phone</code>: Your WhatsApp number (with country code).</li>
                <li><code>msg</code>: Default message shown in WhatsApp chat.</li>
                <li><code>theme</code>: Choose between <code>light</code> or <code>dark</code>.</li>
                <li><code>position</code>: Widget position – e.g., <code>right</code>, <code>center-middle</code>.</li>
                <li><code>autopopup</code>: Delay in milliseconds before the chat opens.</li>
                <li><code>autoclose</code>: Delay to auto-close chat (set 0 to disable).</li>
              </ul>

              <div className="mt-4 text-xs text-gray-500">
                💡 Tip: Add this code in the footer of your site. In WordPress, use plugins like <strong>Insert Headers and Footers</strong> or theme footer options.
              </div>
            </div>
          </>
        )}
      </div>

      <p className="mt-8 text-center text-xs text-gray-400">
        &copy; {new Date().getFullYear()} Developed by{' '}
        <a
          href="https://infotwistsolutions.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-green-600 hover:underline"
          title="ITS - Info Twist Solutions"
        >
          ITS
        </a>
      </p>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);
